/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Grid, Segment, Dropdown, Search } from 'semantic-ui-react';
import {
  getLinkedActivities,
  getLinkedRootOrgStatus,
} from '../../../../../store/actions';
import { get } from '../../../../../utils/lodash';
import { isOnlyProgramCollaborator } from '../../../../../utils/activity/userPermission';
import CreateLinkedActivity from './modals/createLinkedActivity';
import { debounce } from 'lodash';
import { ViewLinkedActivities } from './dataTables/viewLinkedActivities';
import { MULTI_LEVEL_PROGRAM_PERMISSION_NAME } from '../../../../../utils/constant';

function LinkedActivities(props) {
  const { t } = useTranslation();
  const { extraMargin, extraBottomMargin, style } = props;

  const [orderBy, setOrderBy] = useState('asc');
  const [status, setStatus] = useState('all');
  const [sortBy, setSortBy] = useState('activityName');

  const { currentActivity } = useSelector((state) => state.activity);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authentication);

  const ORDERBY_OPTIONS = [
    {
      key: 'asc',
      text: <span>{t('common.ascending')}</span>,
      value: 'asc',
    },
    {
      key: 'desc',
      text: <span>{t('common.descending')}</span>,
      value: 'desc',
    },
  ];

  const STATUS_OPTIONS = [
    {
      key: 'all',
      text: <span>{t('activity_status.all')}</span>,
      value: 'all',
    },
    {
      key: 'completed',
      text: <span>{t('activity_status.completed')}</span>,
      value: 'completed',
    },
    {
      key: 'active',
      text: <span>{t('activity_status.active')}</span>,
      value: 'active',
    },
    {
      key: 'ongoing',
      text: <span>{t('activity_status.ongoing')}</span>,
      value: 'ongoing',
    },
    {
      key: 'projected',
      text: <span>{t('activity_status.projected')}</span>,
      value: 'projected',
    },
    {
      key: 'deactivated',
      text: <span>{t('activity_status.deactivated')}</span>,
      value: 'deactivated',
    },
  ];

  const SORT_BY_OPTIONS = [
    {
      key: 'activityName',
      text: <span>{t('common.activity_name')}</span>,
      value: 'activityName',
    },
    {
      key: 'Status',
      text: <span>{t('common.status')}</span>,
      value: 'status',
    },
    {
      key: 'Contact',
      text: <span>{t('common.contact')}</span>,
      value: 'contact',
    },
    {
      key: 'Organisation',
      text: <span>{t('common.organisation')}</span>,
      value: 'organisationName',
    },
    {
      key: 'Activity Type',
      text: <span>{t('common.activity_type')}</span>,
      value: 'activityType',
    },
  ];

  const reSetFilters = () => {
    setOrderBy('asc');
    setStatus('all');
    setSortBy('activityName');
  };

  const SearchOnchange = (e, { value }) => {
    dispatch(
      getLinkedActivities(currentActivity.id, status, sortBy, orderBy, value)
    );
  };

  const handelOrderBy = (value) => {
    setOrderBy(value);
    dispatch(getLinkedActivities(currentActivity.id, status, sortBy, value));
  };
  const handelStatus = (value) => {
    setStatus(value);
    dispatch(getLinkedActivities(currentActivity.id, value, sortBy, orderBy));
  };

  const handelSortBy = (value) => {
    setSortBy(value);
    dispatch(getLinkedActivities(currentActivity.id, status, value, orderBy));
  };

  const currentUserOnlyProgramCollaborator = isOnlyProgramCollaborator(
    get(user, 'id', null),
    get(currentActivity, 'members', null)
  );

  // TAK-2040: a GUEST who legitimately owns a linked Program (because its
  // root ancestor is organisation-owned) may create further linked
  // activities of any type beneath it, without needing personal feature
  // flags a guest could never be granted — the root-organisation check
  // already ran when this activity was created, and runs again
  // server-side on each new linked activity regardless of what this menu
  // shows. Scoped to guests only (no organisationId) so an ORG user's own
  // activities still respect their org's multi_level_program opt-in as
  // before — this isn't a general "owners skip feature checks" bypass.
  const [isRootOrgOwned, setIsRootOrgOwned] = useState(false);
  useEffect(() => {
    if (currentActivity?.id) {
      dispatch(getLinkedRootOrgStatus(currentActivity.id)).then((result) => {
        setIsRootOrgOwned(!!result?.isRootOrgOwned);
      });
    }
  }, [currentActivity?.id]);
  const canManageAsOwner =
    isRootOrgOwned &&
    get(user, 'id', null) === get(currentActivity, 'ownerId', null) &&
    get(user, 'organisationId', null) == null;

  const currentUserFeatures = get(user, 'features', []);
  const isSingleActivityIncluded =
    canManageAsOwner ||
    currentUserFeatures.some((item) => item === 'single_program');
  const isMultiLevelIncluded =
    canManageAsOwner ||
    currentUserFeatures.some(
      (item) => item === MULTI_LEVEL_PROGRAM_PERMISSION_NAME
    );
  const isFestivalActivityIncluded =
    canManageAsOwner ||
    currentUserFeatures.some((item) => item === 'festival_program');
  const isGrantActivityIncluded =
    canManageAsOwner ||
    currentUserFeatures.some((item) => item === 'grant_program');
  const isMapActivityIncluded =
    canManageAsOwner ||
    currentUserFeatures.some((item) => item === 'multi_activity_program');

  return (
    <Segment.Group
      raised
      style={{
        marginTop: extraMargin ? '2.857rem' : null,
        marginBottom: extraBottomMargin ? '2.857rem' : null,
        ...style,
      }}
    >
      <Segment
        raised
        style={{
          marginTop: true ? '2.857rem' : null,
          marginBottom: false ? '2.857rem' : null,
        }}
        className="evaluation-segment"
      >
        <div className="linkedActivity-eval-plan">
          <Grid>
            <Grid.Column mobile={16} style={{ paddingBottom: '0rem' }}>
              <div className="segment-custom-heading-evidence linked-activity-margin-left">
                {t('common.program_activities')}
                <br></br>
              </div>
            </Grid.Column>
          </Grid>
          <Grid style={{ marginTop: '0rem' }}>
            <Grid.Column mobile={16} tablet={8} computer={8}>
              <label className="eval-plan-desc">
                {t(
                  'activity.activity_plan.form.evaluation_of_this_program_will_be_an_aggregate_of_evaluation_from_the_programs_linked_activity_records_and_reported_through_the_documentation_page_of_this_program'
                )}
              </label>
            </Grid.Column>
            {!currentUserOnlyProgramCollaborator && (
              <Grid.Column
                mobile={16}
                tablet={8}
                computer={8}
                textAlign="right"
                verticalAlign="middle"
              >
                {!isMultiLevelIncluded ? (
                  <CreateLinkedActivity
                    isMultiLevelIncluded={false}
                    textLabel={t('activity_type.single_activity')}
                    modalLabel={`${t('activity_type.single')} {t('activity_type.linked_activity')}`}
                    activityPlanTypeId={1} // activityPlanTypeId is single linked activity
                    parentActivityPlanTypeId={
                      currentActivity.activityPlanTypeId
                    }
                    reSetFilters={reSetFilters}
                    currentState="project.edit.evaluationPlan"
                  />
                ) : (
                  <div className="dropdown-adjust user-profile-dropdown">
                    <Dropdown
                      // disabled={getReportsLoading['evidenceExport']}
                      text={t(
                        'activity.activity_plan.form.add_linked_activity'
                      )}
                      direction="left"
                      selectOnBlur={false}
                      icon="angle down"
                      className="add-linked-btn"
                    >
                      <Dropdown.Menu className="mt-1">
                        {isSingleActivityIncluded && (
                          <CreateLinkedActivity
                            textLabel={t('activity_type.single_activity')}
                            modalLabel={`${t('activity_type.single')} {t('activity_type.linked_activity')}`}
                            activityPlanTypeId={1} // activityPlanTypeId is single linked activity
                            parentActivityPlanTypeId={
                              currentActivity.activityPlanTypeId
                            }
                            reSetFilters={reSetFilters}
                            currentState="project.edit.evaluationPlan"
                          />
                        )}

                        {/* Grant Program */}
                        {isMultiLevelIncluded && isGrantActivityIncluded && (
                          <CreateLinkedActivity
                            textLabel={t('activity_type.grants_program')}
                            modalLabel={`${t('activity_type.grants_program')} {t('activity_type.linked_activity')}`}
                            activityPlanTypeId={4}
                            reSetFilters={reSetFilters}
                            currentState="project.edit.evaluationPlan"
                          />
                        )}

                        {/* Festival Program */}
                        {isMultiLevelIncluded && isFestivalActivityIncluded && (
                          <CreateLinkedActivity
                            textLabel={t('activity_type.festival_program')}
                            modalLabel={`${t('activity_type.festival_program')} {t('activity_type.linked_activity')}`}
                            activityPlanTypeId={3}
                            parentActivityPlanTypeId={
                              currentActivity.activityPlanTypeId
                            }
                            reSetFilters={reSetFilters}
                            currentState="project.edit.evaluationPlan"
                          />
                        )}

                        {/* MAP Program */}
                        {isMultiLevelIncluded && isMapActivityIncluded && (
                          <CreateLinkedActivity
                            textLabel={t(
                              'activity_type.multi_activity_program'
                            )}
                            modalLabel={`${t('activity_type.multi_activity_program')} {t('activity_type.linked_activity')}`}
                            activityPlanTypeId={6}
                            parentActivityPlanTypeId={
                              currentActivity.activityPlanTypeId
                            }
                            reSetFilters={reSetFilters}
                            currentState="project.edit.evaluationPlan"
                          />
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                )}
              </Grid.Column>
            )}
          </Grid>
          <Grid>
            <Grid.Column mobile={16} tablet={16} computer={5}>
              <Search
                className="search-bar-custom linked-activity-margin-left"
                // style={{ marginLeft: '1.125rem' }}
                placeholder={t(
                  'activity.activity_plan.search_by_activity_name'
                )}
                showNoResults={false}
                onSearchChange={debounce(SearchOnchange, 500)}
              />
            </Grid.Column>
            <Grid.Column
              mobile={16}
              tablet={16}
              computer={11}
              textAlign="right"
              verticalAlign="middle"
            >
              <div className="org-sort">
                <span className="activities-filter">
                  {t('common.sort_by')}: &nbsp;
                  <Dropdown
                    direction="left"
                    inline
                    selectOnBlur={false}
                    value={sortBy}
                    options={SORT_BY_OPTIONS}
                    onChange={(e, { value }) => handelSortBy(value)}
                  />
                </span>
                <span className="activities-filter linked-activity-margin-left">
                  {t('common.order_by')}: &nbsp;
                  <Dropdown
                    direction="left"
                    inline
                    value={orderBy}
                    selectOnBlur={false}
                    options={ORDERBY_OPTIONS}
                    onChange={(e, { value }) => handelOrderBy(value)}
                  />
                </span>
                <span className="activities-filter linked-activity-margin-left">
                  {t('common.status')}: &nbsp;
                  <Dropdown
                    direction="left"
                    inline
                    value={status}
                    selectOnBlur={false}
                    options={STATUS_OPTIONS}
                    onChange={(e, { value }) => handelStatus(value)}
                  />
                </span>
              </div>
            </Grid.Column>
          </Grid>
        </div>
        <Grid>
          <Grid.Column
            mobile={16}
            style={{ margin: '0rem', padding: '0rem 0rem' }}
          >
            <ViewLinkedActivities
              reSetFilters={reSetFilters}
              currentState="project.edit.evaluationPlan"
            />
          </Grid.Column>
        </Grid>
      </Segment>
    </Segment.Group>
  );
}

export default LinkedActivities;
